<template>
  <Layout padding-horizontal="0" padding-vertical="0">
    <div class="main-container">
      <div class="column-container left-column">
        <div class="page-header">
          <h2 class="page-title">{{$t('create_new_content')}}</h2>
          <h5 class="page-description">{{$t('marketplace_description')}}</h5>
        </div>

        <form @submit.prevent="handleSubmit" class="create-form">

          <div class="form-section">
            <h3 class="section-title">{{$t('basic_informations')}}</h3>
            <div class="form-group">
              <label class="form-label">{{ $t('title') }} *</label>
              <input
                v-model="form.title"
                type="text"
                class="form-input"
                :placeholder="$t('enter_product_title')"
                required
              />
            </div>

            <div class="form-group">
              <label class="form-label">{{ $t('description') }} *</label>
              <textarea
                  v-model="form.description"
                  class="form-input"
                  rows="4"
                  :placeholder="$t('enter_product_description')"
                  required
              ></textarea>
            </div>

            <div class="form-group">
              <label class="form-label">{{ $t('category') }} *</label>
              <SearchableSelectBox
                  v-model="form.marketplaceCategoryId"
                  :options="categoryOptions"
                  :placeholder="$t('select_category')"
                  :loading="loadingCategories"
                  @search="searchCategories"
                  required
              />
            </div>

            <div class="form-group">
              <label class="form-label">{{ $t('topics_to_learn') }}</label>
              <div class="topics-input-container">
                <input
                    type="text"
                    v-model="topicInput"
                    @keypress.enter.prevent="addTopic"
                    :placeholder="$t('type_topic_press_enter')"
                    class="form-input"
                />
                <button
                    type="button"
                    @click="addTopic"
                    class="dark-button-sm"
                    :disabled="!topicInput.trim()"
                >
                  {{ $t('add') }}
                </button>
              </div>

              <div v-if="selectedTopics.length > 0" class="selected-topics-display">
                <div
                    v-for="(topic, index) in selectedTopics"
                    :key="index"
                    class="topic-tag"
                >
                  {{ topic }}
                  <button type="button" @click="removeTopic(index)" class="remove-topic">×</button>
                </div>
              </div>
            </div>
          </div>


          <div class="form-section">
            <h3 class="section-title">{{ $t('product_selection') }}</h3>

            <div class="form-group">
              <label class="form-label">{{ $t('module_type') }} *</label>
              <SearchableSelectBox
                  v-model="form.moduleId"
                  :options="moduleTypeOptions"
                  :placeholder="$t('select_module_type')"
                  required
              />
            </div>
            <div class="form-group" v-if="form.moduleId">
              <label class="form-label">{{ $t('select_product') }} *</label>
              <SearchableSelectBox
                v-model="form.productId"
                :options="productOptions"
                :placeholder="getProductPlaceholder()"
                :loading="loadingProducts"
                @search="searchProducts"
                required
              />
            </div>
          </div>

          <div class="form-section">
            <h3 class="section-title">{{ $t('pricing') }}</h3>

            <div class="form-group">
              <div class="toggle-group">
                <Switch v-model="form.isFree" />
                <label class="toggle-label">{{ $t('free_product') }}</label>
              </div>
            </div>

            <div v-if="!form.isFree" class="pricing-row">
              <div class="form-group">
                <label class="form-label">{{ $t('price') }} *</label>
                <input
                  v-model="form.price"
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-input"
                  :placeholder="$t('enter_price')"
                  required
                />
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('currency') }} *</label>
                <SelectBox
                  v-model="form.currency"
                  :options="currencyOptions"
                  :placeholder="$t('select_currency')"
                  required
                  class="full-width"
                />
              </div>
            </div>
          </div>

          <div class="form-section">
            <h3 class="section-title">{{$t('sharing_choices')}}</h3>

            <div class="date-row">
              <div class="form-group">
                <label class="form-label">{{ $t('start_date') }} *</label>
                <input
                  v-model="form.startDate"
                  type="datetime-local"
                  class="form-input"
                  required
                />
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('end_date') }} *</label>
                <input
                  v-model="form.endDate"
                  type="datetime-local"
                  class="form-input"
                  required
                />
              </div>
            </div>
          </div>

          <!-- Images Section -->
          <div class="form-section">
            <h3 class="section-title">
              {{$t('product_image')}}
            </h3>

            <div class="form-group">
              <FileDrop :on-file-selected="handleImagesUpload" />


              <div v-if="form.images.length > 0" class="image-preview-grid">
                <div
                  v-for="(image, index) in form.images"
                  :key="index"
                  class="image-preview-item"
                >
                  <img :src="getImagePreview(image)" :alt="`Image ${index + 1}`" />
                  <button
                    type="button"
                    @click="removeImage(index)"
                    class="remove-image-btn"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Global Share Section -->
          <div class="form-section">
            <h3 class="section-title">{{ $t('sharing_settings') }}</h3>

            <div class="form-group">
              <div class="toggle-group">
                <Switch v-model="shareGlobally" />
                <label class="toggle-label">{{ $t('share_globally') }}</label>
                <p class="toggle-description">{{ $t('share_globally_description') }}</p>
              </div>
            </div>
          </div>

        </form>
      </div>

      <!-- Right Column - Preview -->
      <div class="column-container right-column">
        <div class="preview-title-section">
          <span class="preview-title">
            {{$t('preview_the_product')}}
          </span>

        </div>
        <div class="alert-section">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.60509 3.74338L12.0004 2.09998L19.3957 3.74338C19.5955 3.7878 19.7743 3.89904 19.9024 4.05874C20.0305 4.21844 20.1004 4.41704 20.1004 4.62178V13.6101C20.1003 14.499 19.8808 15.3742 19.4613 16.158C19.0418 16.9417 18.4353 17.6098 17.6956 18.1029L12.0004 21.9L6.30519 18.1029C5.56564 17.6099 4.95921 16.942 4.5397 16.1584C4.1202 15.3748 3.9006 14.4998 3.90039 13.611V4.62178C3.90043 4.41704 3.97026 4.21844 4.09838 4.05874C4.2265 3.89904 4.40523 3.7878 4.60509 3.74338ZM5.70039 5.34358V13.6101C5.7004 14.2027 5.84671 14.7861 6.12633 15.3086C6.40594 15.8311 6.81023 16.2765 7.30329 16.6053L12.0004 19.7373L16.6975 16.6053C17.1904 16.2766 17.5946 15.8314 17.8742 15.309C18.1539 14.7867 18.3002 14.2034 18.3004 13.611V5.34358L12.0004 3.94498L5.70039 5.34358ZM12.0004 11.1C11.4037 11.1 10.8314 10.8629 10.4094 10.441C9.98744 10.019 9.75039 9.44671 9.75039 8.84998C9.75039 8.25324 9.98744 7.68094 10.4094 7.25899C10.8314 6.83703 11.4037 6.59998 12.0004 6.59998C12.5971 6.59998 13.1694 6.83703 13.5914 7.25899C14.0133 7.68094 14.2504 8.25324 14.2504 8.84998C14.2504 9.44671 14.0133 10.019 13.5914 10.441C13.1694 10.8629 12.5971 11.1 12.0004 11.1ZM7.97469 15.6C8.08379 14.6085 8.55491 13.6922 9.29776 13.0266C10.0406 12.361 11.003 11.993 12.0004 11.993C12.9978 11.993 13.9602 12.361 14.703 13.0266C15.4459 13.6922 15.917 14.6085 16.0261 15.6H7.97469Z" fill="#525866"/>
          </svg>
          <span class="alert-title">{{$t('product_sent_for_approval')}}</span>

        </div>
        <div class="preview-section">

          <div class="preview-card">
            <div class="preview-image">
              <img
                v-if="form.images.length > 0"
                :src="getImagePreview(form.images[0])"
                alt="Product preview"
                class="preview-img"
              />
              <div v-else class="preview-placeholder">
                <img src="@/assets/images/ibility-Logo.svg" alt="placeholder" />
                <span>{{ $t('no_image') }}</span>
              </div>
            </div>

            <div class="preview-content">
              <!-- Category and Date Row -->
              <div class="preview-meta-row">
                <span class="preview-category">
                  {{ getCategoryName() || this.$t('category') }}
                </span>
                <span class="preview-date" v-if="form.startDate">
                  {{ formatPreviewDate(form.endDate) }}
                </span>

              </div>

              <!-- Title -->
              <h4 class="preview-product-title">
                {{ form.title || $t('product_title_placeholder') }}
              </h4>

              <!-- Description -->
              <p class="preview-description">
                {{ truncateDescription(form.description) || $t('product_description_placeholder') }}
              </p>

              <!-- Topics -->
              <div class="preview-topics" v-if="selectedTopics.length > 0">
                <div class="preview-topics-list">
                  <span
                    v-for="topic in selectedTopics.slice(0, 3)"
                    :key="topic"
                    class="preview-topic"
                  >
                    {{ topic }}
                  </span>
                  <span v-if="selectedTopics.length > 3" class="more-topics">
                    +{{ selectedTopics.length - 3 }} {{ $t('more') }}
                  </span>
                </div>
              </div>

              <!-- Price -->
              <div class="preview-price">
                <span v-if="form.isFree" class="price-value">{{$t('free')}}</span>
                <span v-else-if="form.price" class="price-value">
                  {{ form.price }} {{ form.currency }}
                </span>
                <span v-else class="price-value">{{ $t('price_not_set') }}</span>
              </div>
            </div>

          </div>
          <div
            class="send-to-review-section"
            :class="{ 'disabled': !isBasicInfoComplete || isSubmitting }"
            @click="handleSubmitFromPreview"
          >
            <span class="send-to-review-text">
              <span v-if="isSubmitting">{{$t('product_is_sending')}}...</span>
              <span v-else>{{$t('send_product_for_approval')}}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/components/layouts/Layout.vue";
import SearchableSelectBox from "@/components/SearchableSelectBox.vue";
import SelectBox from "@/components/SelectBox.vue";
import Switch from "@/components/Switch.vue";
import FileDrop from "@/components/FileDrop.vue";
import marketplaceService from "@/service/marketplaceService";
import marketplaceCategoriesService from "@/service/marketplaceCategoriesService";
import marketplaceModuleService from "@/service/marketplaceModuleService";
import testService from "../service/testService";
import programService from '../service/programService';
import { handleErrorResponse } from "@/utils/utils";
import gameLabService from "../service/gameLabService";
import TextInput from "@/components/TextInput.vue";

export default {
  name: 'MarketplaceCreate',
  components: {
    TextInput,
    Layout,
    SearchableSelectBox,
    SelectBox,
    Switch,
    FileDrop
  },
  data() {
    return {
      form: {
        title: '',
        description: '',
        marketplaceCategoryId: '',
        moduleId: '',
        productId: '',
        price: '',
        currency: 'TRY',
        isFree: false,
        startDate: '',
        endDate: '',
        images: [],
        onlyCompanies: false,
        topicsToLearn: []
      },

      categoryOptions: [],
      moduleTypeOptions: [],
      productOptions: [],
      currencyOptions: [
        { value: 'TRY', text: 'TRY - Turkish Lira' },
        { value: 'USD', text: 'USD - US Dollar' },
        { value: 'EUR', text: 'EUR - Euro' }
      ],

      loadingCategories: false,
      loadingProducts: false,
      isSubmitting: false,

      moduleTypeMapping: {
        '5d922f3f-5d9b-4d5c-8880-d0c90e6e5d18': 'test',
        '37f57ccf-6486-4de7-b6b7-bd326cd3a561': 'program',
        '7c008460-9e4b-41eb-96e6-c5d4b1222189': 'gamelabs'
      },

      selectedTopics: [],
      topicInput: ''
    }
  },
  computed: {
    shareGlobally: {
      get() {
        return !this.form.onlyCompanies;
      },
      set(value) {
        this.form.onlyCompanies = !value;
      }
    },
    isBasicInfoComplete() {
      return this.form.title.trim() !== '' &&
             this.form.description.trim() !== '' &&
             this.form.marketplaceCategoryId !== '';
    }
  },
  async created() {
    await this.loadInitialData();
    this.setDefaultDates();
  },
  watch: {
    'form.moduleId'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.onModuleTypeChange();
      }
    },
    'form.productId'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.loadProducts();
      }

    }
  },
  methods: {
    async loadInitialData() {
      await Promise.all([
        this.loadCategories(),
        this.loadModuleTypes()
      ]);
    },

    truncateDescription(description) {
      if (!description) return '';
      return description.length > 30 ? description.substring(0, 30) + '...' : description;
    },

    async loadCategories() {
      try {
        this.loadingCategories = true;
        const response = await marketplaceCategoriesService.getCategories({
          page: 1,
          perPage: 100
        });

        const categories = response.data?.data?.data || response.data?.data || [];

        this.categoryOptions = categories.map(category => ({
          value: category.uuid,
          text: category.title
        }));
      } catch (error) {
        console.error('Failed to load categories:', error);
        this.$snackbar.add({
          type: 'error',
          text: this.$t('failed_to_load_categories')
        });
      } finally {
        this.loadingCategories = false;
      }
    },

    async loadModuleTypes() {
      try {
        const response = await marketplaceModuleService.store({
          page: 1,
          perPage: 100
        });

        const modules = response.data?.data?.data || response.data?.data || [];

        this.moduleTypeOptions = modules.map(module => ({
          value: module.uuid,
          text: module.title || module.moduleName
        }));
      } catch (error) {
        console.error('Failed to load module types:', error);
        this.$snackbar.add({
          type: 'error',
          text: this.$t('failed_to_load_modules')
        });
      }
    },

    async searchCategories(searchText) {
      if (!searchText) {
        this.loadCategories();
        return;
      }

      try {
        this.loadingCategories = true;
        const response = await marketplaceCategoriesService.getCategories({
          page: 1,
          perPage: 50,
          search: searchText
        });

        const categories = response.data?.data?.data || response.data?.data || [];
        this.categoryOptions = categories.map(category => ({
          value: category.uuid,
          text: category.title
        }));
      } catch (error) {
        console.error('Failed to search categories:', error);
      } finally {
        this.loadingCategories = false;
      }
    },

    async onModuleTypeChange() {
      this.form.productId = '';
      this.productOptions = [];

      if (this.form.moduleId) {
        await this.loadProducts();
      }
    },

    async loadProducts() {
      const moduleType = this.moduleTypeMapping[this.form.moduleId];

      if (!moduleType) return;

      try {
        this.loadingProducts = true;
        let response;

        switch (moduleType) {
          case 'test':
            response = await testService.getTests(1);
            break;
          case 'program':
            response = await programService.getPrograms(1);
            break;
          case 'gamelabs':
            response = await gameLabService.getGameLabs(1);
            break;
          default:
            return;
        }

        const products = response.data.data?.data || response.data?.data || [];

        this.productOptions = products.map((product, index) => {
          const value = product.uuid ||
                       product.programId ||
                       product.testId ||
                       product.gameLabsId ||
                       product.id ||
                       `product_${index}`;

          let text;
          switch (moduleType) {
            case 'test':
              text = product.testName || product.name || product.title || `Test ${index + 1}`;
              break;
            case 'program':
              text = product.name || product.title || product.programName || `Program ${index + 1}`;
              break;
            case 'gamelabs':
              text = product.name || product.title || product.gameLabName || `GameLab ${index + 1}`;
              break;
            default:
              text = product.title || product.name || `Product ${index + 1}`;
              break;
          }

          return { value, text };
        });
      } catch (error) {
        console.error(`Failed to load ${moduleType} products:`, error);
        this.$snackbar.add({
          type: 'error',
          text: this.$t('failed_to_load_products')
        });
      } finally {
        this.loadingProducts = false;
      }
    },

    async searchProducts(searchText) {
      const moduleType = this.moduleTypeMapping[this.form.moduleId];
      if (!moduleType) return;

      try {
        this.loadingProducts = true;
        let response;

        switch (moduleType) {
          case 'test':
            response = await testService.getTests(1, [{
              key: "searchText",
              value: searchText,
            }]);
            break;
          case 'program':
            response = await programService.getPrograms(1, [{
              key: "searchText",
              value: searchText,
            }]);
            break;
          case 'gamelabs':
            response = await gameLabService.getGameLabs(1, [{
              key: "searchText",
              value: searchText,
            }]);
            break;
          default:
            return;
        }

        const products = response.data.data?.data || [];

        this.productOptions = products.map((product, index) => {
          const value = product.uuid ||
                       product.programId ||
                       product.testId ||
                       product.gameLabsId ||
                       product.id ||
                       `product_${index}`;

          let text;
          switch (moduleType) {
            case 'test':
              text = product.testName || product.name || product.title || `Test ${index + 1}`;
              break;
            case 'program':
              text = product.name || product.title || product.programName || `Program ${index + 1}`;
              break;
            case 'gamelabs':
              text = product.name || product.title || product.gameLabName || `GameLab ${index + 1}`;
              break;
            default:
              text = product.title || product.name || `Product ${index + 1}`;
              break;
          }

          return { value, text };
        });
      } catch (error) {
        console.error('Failed to search products:', error);
      } finally {
        this.loadingProducts = false;
      }
    },

    getProductPlaceholder() {
      const moduleType = this.moduleTypeMapping[this.form.moduleId];
      switch (moduleType) {
        case 'test':
          return this.$t('select_test');
        case 'program':
          return this.$t('select_program');
        case 'gamelabs':
          return this.$t('select_gamelab');
        default:
          return this.$t('select_product');
      }
    },

    setDefaultDates() {
      const now = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1);

      this.form.startDate = this.formatDateForInput(now);
      this.form.endDate = this.formatDateForInput(endDate);
    },

    formatDateForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },

    handleImagesUpload(file) {
      this.form.images = [...this.form.images, file];
    },

    removeImage(index) {
      this.form.images.splice(index, 1);
    },

    getImagePreview(file) {
      return URL.createObjectURL(file);
    },

    formatDisplayDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatPreviewDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },

    getCategoryName() {
      if (!this.form.marketplaceCategoryId) return '';
      const category = this.categoryOptions.find(cat => cat.value === this.form.marketplaceCategoryId);
      return category ? category.text : '';
    },

    addTopic() {
      const newTopic = this.topicInput.trim();
      if (newTopic && !this.selectedTopics.includes(newTopic)) {
        this.selectedTopics.push(newTopic);
        this.form.topicsToLearn = [...this.selectedTopics];
        this.topicInput = '';
      }
    },

    removeTopic(index) {
      this.selectedTopics.splice(index, 1);
      this.form.topicsToLearn = [...this.selectedTopics];
    },

    handleSubmitFromPreview() {
      if (!this.isBasicInfoComplete || this.isSubmitting) {
        return;
      }
      this.handleSubmit();
    },

    async handleSubmit() {
      try {
        this.isSubmitting = true;

        const formData = new FormData();

        formData.append('title', this.form.title);
        formData.append('description', this.form.description);
        formData.append('marketplaceCategoryId', this.form.marketplaceCategoryId);
        formData.append('moduleId', this.form.moduleId);
        formData.append('productId', this.form.productId);

        if (this.form.isFree) {
          formData.append('price', '0');
          formData.append('currency', this.form.currency || 'TRY');
        } else {
          formData.append('price', this.form.price);
          formData.append('currency', this.form.currency);
        }

        formData.append('startDate', this.form.startDate);
        formData.append('endDate', this.form.endDate);

        this.form.images.forEach((image, index) => {
          formData.append(`images`, image);
        });

        this.selectedTopics.forEach((topic, index) => {
          formData.append(`topicsToLearn[${index}]`, topic);
        });

        formData.append('onlyCompanies', this.form.onlyCompanies ? '1' : '0');

        const response = await marketplaceService.add(formData);

        this.$router.push('/marketplace/success');

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      } finally {
        this.isSubmitting = false;
      }
    }
  }
}
</script>

<style scoped>
/* Main Layout */
.main-container {
  display: flex;
  background: #F5F5F5;
}
.send-to-review-section{
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
  background: #040C2D;
  border-radius: 20px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.send-to-review-section:hover:not(.disabled) {
  background: #0D1A47;
}

.send-to-review-section.disabled {
  background: #EAECF0;
  cursor: not-allowed;
}
.send-to-review-text{
  font-size: 16px;
  font-weight: 600;
  color: #FFFFFF;
  font-family: 'euclid_medium', sans-serif;
  transition: color 0.3s ease;
}

.send-to-review-section.disabled .send-to-review-text {
  color: #D0D5DD;
}
.alert-section{
  background: #F9FAFB;
  border-radius: 8px;
  margin-bottom: 16px;
  padding-left: 12px;
}
.alert-title{
  font-size: 12px;
  color: #667085;
  font-weight: 400;
  font-family: 'euclid_medium', sans-serif;
}
.price-value{
  color: #040C2D;
  font-size: 20px;

}

.column-container {
  padding: 32px;
}

.left-column {
  width: 70%;
}

.page-header {
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: flex-start;
}

.page-title {
  font-size: 20px;
  font-weight: 500;
  color: #101828;
  font-family: 'euclid_medium', sans-serif;
}

.page-description {
  font-size: 14px;
  color: #475467;
  font-family: 'euclid_medium', sans-serif;
}

.create-form {
  border-radius: 12px;
  padding: 24px;
  gap: 10px;
}

.form-section {
  margin-bottom: 32px;
  border-bottom: 1px solid #EAECF0;
  background: #FFFFFF;
  padding: 20px;
  border-radius: 12px;
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #040C2D;
  border-bottom: 1px solid #EAECF0;
  padding-bottom: 12px;
  margin-bottom: 16px;
  font-family: 'euclid_medium', sans-serif;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
  font-family: 'euclid_medium', sans-serif;
}

.toggle-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  font-size: 14px;
  font-weight: 500;
  color: #1c1d1f;
  margin: 0;
}

.pricing-row, .date-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.toggle-description {
  font-size: 12px;
  color: #6c757d;
  margin: 4px 0 0 0;
  line-height: 1.4;
}

.image-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.image-preview-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.image-preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-image-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.topics-input-container {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.topics-input {
  flex: 1;
}

.selected-topics-display {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.topic-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.remove-topic {
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  padding: 0;
  line-height: 1;
}

.remove-topic:hover {
  color: #d32f2f;
}

.preview-section {
  padding: 15px;
  background: white;
  border-radius: 12px;

}

.preview-title {
  font-size: 20px;
  font-weight: 500;
  color: #101828;
  font-family: 'euclid_medium', sans-serif;
}
.preview-title-section{
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  border-bottom: 1px solid #EAECF0;
}

.preview-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #E5E7EB;
}

.preview-image {
  width: 100%;
  height: 180px;
  background: #F3F4F6;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #9CA3AF;
  padding: 24px;
}

.preview-placeholder img {
  width: 64px;
  height: 64px;
  opacity: 0.3;
}

.preview-placeholder span {
  font-size: 12px;
  font-weight: 500;
  text-align: center;
}

.preview-content {
  padding: 16px;
}

.preview-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
}

.preview-category {
  background: #F8F9FA;
  color: #6C757D;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-date {
  color: #6C757D;
  font-size: 12px;
  font-weight: 400;
}

.preview-product-title {
  font-size: 16px;
  font-weight: 600;
  color: #040C2D;
  margin-bottom: 8px;
  line-height: 1.4;
  font-family: 'euclid_medium', sans-serif;
  overflow-wrap: break-word;
}

.preview-description {
  font-size: 13px;
  color: #6C757D;
  margin-bottom: 12px;
  line-height: 1.5;
  font-family: 'euclid_medium', sans-serif;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow-wrap: break-word;

}

.preview-topics {
  margin-bottom: 16px;
}

.preview-topics-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.preview-topic {
  background: #E3F2FD;
  color: #1565C0;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
}

.more-topics {
  background: #F5F5F5;
  color: #757575;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
}

.preview-price {
  display: flex;
  justify-content: center;
  border: 1px solid #98A2B3;
  margin-top: auto;
  padding: 4px;
  border-radius:20px;
}

.price-value {
  font-size: 18px;
  font-weight: 700;
  font-family: 'euclid_medium', sans-serif;
  color: #040C2D;
}

.setting-header h4 {
  font-size: 14px;
  font-weight: 600;
  color: #040C2D;
  margin: 0;
  font-family: 'euclid_medium', sans-serif;
}

.right-column{
  background: white;
  height: fit-content;
  border-radius: 12px;
  margin-top: 8%;
  position: sticky;
  top: 32px;
  overflow: hidden;
  max-width: 480px;
}

@media (max-width: 1024px) {
  .main-container {
    flex-direction: column;
  }

  .left-column, .right-column {
    max-width: 100%;
  }

  .left-column {
    border-right: none;
    border-bottom: 1px solid #EAECF0;
  }

  .right-column {
    position: static;
    margin-top: 24px;
    max-height: none;
    overflow: visible;
  }
}

@media (max-width: 768px) {
  .column-container {
    padding: 16px;
  }

  .create-form {
    padding: 16px;
  }

  .pricing-row, .date-row {
    grid-template-columns: 1fr;
  }

  .preview-image {
    height: 150px;
  }

  .preview-content {
    padding: 16px;
  }
}
</style>
